import { effect, inject, Injectable, linkedSignal } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { toast } from 'ngx-sonner';
import { Subject } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';

import { LivrableCaractérisé } from '../../livrables-caractérisés/model/livrable-caracterisé.model';
import { FormControlNames, FormGroupNames } from '../../référentiel-métier-parcours/models/form-control-names.model';
import { ModuleStore } from '../../référentiel-métier-parcours/parcours-module/store/module.store';
import { Aptitude } from '../../référentiel-métier/référentiel-métier-processus/processus-aptitude/models/aptitude.model';
import { ToastrErrorComponent } from '../../shared/components/toastr/toastr-error.component';
import { Activité } from '../models/activité.model';
import { TypeSequence } from '../models/séquence.model';
import { ÉtatSéquence } from '../models/état-séquence.enum';
import { SequenceStore } from '../store/sequence.store';

@Injectable({
  providedIn: 'root',
})
export class SéquenceFormService {
  readonly #fb = inject(FormBuilder);
  readonly #séquenceStore = inject(SequenceStore);
  readonly #moduleStore = inject(ModuleStore);
  readonly #router = inject(Router);

  readonly séquenceSélectionnée = linkedSignal(() => this.#séquenceStore.selectedEntity());
  readonly moduleSelectionne = linkedSignal(() => this.#moduleStore.selectedEntity());

  aptitudesDisponibles = linkedSignal<Aptitude[]>(() => {
    const module = this.moduleSelectionne();
    const séquence = this.séquenceSélectionnée();
    let aptitudes = module?.aptitudes ?? [];
    if (séquence && module && module.aptitudes.length > 0) {
      aptitudes = aptitudes.filter((x) => !séquence.aptitudes?.some((y) => x.id === y.id));
    }
    return aptitudes;
  });
  readonly reload = new Subject();

  form: FormGroup = this.#fb.group({
    [FormGroupNames.GENERAL]: this.#fb.group({
      [FormControlNames.NOM]: [null],
      [FormControlNames.CODE]: [{ value: null, disabled: true }],
      [FormControlNames.DUREE]: [null],
      [FormControlNames.TYPE]: [null],
      [FormControlNames.MODALITES]: [null],
      [FormControlNames.CONTEXTE]: [null],
    }),
    [FormGroupNames.OBJECTIFS]: this.#fb.array([]),
    [FormControlNames.LIVRABLES]: [[]],
    [FormControlNames.ACTIVITES]: [[]],
  });

  constructor() {
    effect(() => {
      const sequence = this.séquenceSélectionnée();
      const module = this.moduleSelectionne();
      if (sequence && module) {
        this.form.enable({ emitEvent: false });
        this.objectifs.clear({ emitEvent: false });
        this.form.patchValue(
          {
            [FormGroupNames.GENERAL]: {
              [FormControlNames.NOM]: sequence.nom ?? null,
              [FormControlNames.CODE]: sequence.codeSequence,
              [FormControlNames.DUREE]: sequence.duree ?? null,
              [FormControlNames.TYPE]: sequence.type,
              [FormControlNames.MODALITES]: sequence.modalites ?? null,
              [FormControlNames.CONTEXTE]: sequence.contexte ?? null,
            },
            [FormGroupNames.OBJECTIFS]: [],
            [FormControlNames.LIVRABLES]:
              sequence.livrables && sequence.livrables.length > 0 ? sequence.livrables : module.livrablesCaracterises,
            [FormControlNames.ACTIVITES]: sequence.activites ?? [],
          },
          { emitEvent: false },
        );

        sequence.aptitudes?.forEach((x) => this.addAptitude(x));
        this.reload.next(true);

        if (sequence.etat === ÉtatSéquence.TERMINEE || sequence.etat === ÉtatSéquence.DESACTIVEE) {
          this.form.disable({ emitEvent: false });
        } else {
          this.codeSequence.disable({ emitEvent: false });
        }
      }
    });
  }

  addAptitude(aptitude: Aptitude) {
    const séquence = this.séquenceSélectionnée();
    if (!séquence) return;

    const aptitudeEvaluée = séquence.aptitudesEvaluees?.find((x) => x.idAptitude === aptitude.id);

    const interrelationsDeLAptitude = séquence.interrelations
      .filter((interrelation) => interrelation.idAptitude === aptitude.id)
      .map((x) => ({ id: `${x.idAptitude}-${x.idInterrelation}` }));

    const productionsDeLAptitude = séquence.productions?.filter((production) =>
      aptitude.productions?.some((p) => p.id === production.id),
    );

    const objectifForm = this.#fb.group({
      aptitude: [aptitude],
      interrelations: [interrelationsDeLAptitude],
      productions: [productionsDeLAptitude],
      estEvaluee: [aptitudeEvaluée !== undefined],
      objectif: [aptitudeEvaluée?.objectifPedagogique],
    });

    this.objectifs.push(objectifForm, { emitEvent: false });
  }

  marquerCommeTerminée() {
    this.nom.setValidators([Validators.required]);
    this.nom.updateValueAndValidity({ emitEvent: false });
    this.duree.setValidators([Validators.required]);
    this.duree.updateValueAndValidity({ emitEvent: false });
    this.modalites.setValidators([Validators.required]);
    this.modalites.updateValueAndValidity({ emitEvent: false });
    this.activites.setValidators([Validators.required]);
    this.activites.updateValueAndValidity({ emitEvent: false });

    const toastrId = uuidv7();

    if (this.form.invalid) {
      this.form.markAllAsTouched({ emitEvent: false });
      toast.custom(ToastrErrorComponent, {
        componentProps: {
          message: 'Vous devez remplir les champs mentionnés comme obligatoires pour pouvoir enregistrer.',
          titre: "Impossible d'enregistrer",
          id: toastrId,
        },
        id: toastrId,
      });
      return;
    }
    const sequence = this.séquenceSélectionnée();
    const idSéquence = sequence?.id;
    if (idSéquence && idSéquence !== 'null') {
      this.#séquenceStore.modifier({
        id: idSéquence,
        data: {
          ...sequence,
          etat: ÉtatSéquence.TERMINEE,
        },
        next: () => {},
      });
    }
  }

  desactiver() {
    const sequence = this.séquenceSélectionnée();
    const idSéquence = sequence?.id;
    if (idSéquence && idSéquence !== 'null') {
      this.#séquenceStore.modifier({
        id: idSéquence,
        data: {
          ...sequence,
          etat: ÉtatSéquence.DESACTIVEE,
        },
        next: () => {},
      });
    }
  }

  submit() {
    const sequence = this.séquenceSélectionnée();
    const idModule = this.#moduleStore.selectedEntityId()?.toString();

    if (!sequence || !idModule || sequence.etat === ÉtatSéquence.TERMINEE) return;

    const idSéquence = sequence.id;

    if (idSéquence !== 'null') {
      this.#séquenceStore.modifier({
        id: idSéquence,
        data: {
          nom: this.nom.value,
          indice: sequence.indice!,
          duree: this.duree.value,
          modalites: this.modalites.value,
          type: this.type.value,
          idsAptitudes: this.objectifs.value?.flatMap((x) => x.aptitude.id),
          aptitudesInterrelations: [
            ...this.objectifs?.value
              .flatMap((x) => x.interrelations as { id: string }[])
              .map((x) => x.id.split('-', 2))
              .map(([idAptitude, idInterrelation]) => ({ idAptitude, idInterrelation })),
          ],
          idsProductions: [...new Set(this.objectifs?.value.flatMap((x) => x.productions).map((x) => x.id))],
          etat: ÉtatSéquence.EN_COURS,
          contexte: this.contexte.value,
          activites: this.activites.value,
          //// idsLivrablesCaracterises: this.livrables.value,
          aptitudesEvaluees: this.objectifs.value
            .filter((x) => x.estEvaluee)
            .map((x) => ({ idAptitude: x.aptitude.id, objectifPedagogique: x.objectif })),
        },
      });
    } else {
      this.#séquenceStore.ajouterUneSequence({
        newSequence: {
          nom: this.nom.value,
          indice: sequence.indice,
          duree: this.duree.value,
          modalites: this.modalites.value,
          type: this.type.value,
          idModule: idModule,
        },
        next: () => {
          const sequence = this.#séquenceStore.selectedEntity();
          if (sequence) {
            this.#router.navigate([], { queryParams: { idSequence: sequence.id }, queryParamsHandling: 'replace' });
            this.form.reset(null, { emitEvent: false });
            this.#séquenceStore.charger(idModule);
          }
        },
      });
    }
  }

  // Form Groups
  get general(): FormGroup {
    return this.form.get(FormGroupNames.GENERAL) as FormGroup;
  }

  get objectifs(): FormArray<FormGroup> {
    return this.form.get(FormGroupNames.OBJECTIFS) as FormArray<FormGroup>;
  }

  // Général
  get nom() {
    return this.general.get(FormControlNames.NOM) as FormControl<string>;
  }
  get codeSequence() {
    return this.general.get(FormControlNames.CODE) as FormControl<string>;
  }
  get duree() {
    return this.general.get(FormControlNames.DUREE) as FormControl<number>;
  }
  get type() {
    return this.general.get(FormControlNames.TYPE) as FormControl<TypeSequence>;
  }
  get modalites() {
    return this.general.get(FormControlNames.MODALITES) as FormControl<string>;
  }
  get contexte() {
    return this.general.get(FormControlNames.CONTEXTE) as FormControl<string>;
  }

  // Livrables
  get livrables() {
    return this.form.get(FormControlNames.LIVRABLES) as FormControl<LivrableCaractérisé[]>;
  }

  // Activités
  get activites() {
    return this.form.get(FormControlNames.ACTIVITES) as FormControl<Activité[]>;
  }
}
